import { Component } from '@theme/component';

/**
 * Hero-slide image gallery — a self-contained crossfade rotator for the media side of the
 * `_hero-slide-gallery` block.
 *
 * Deliberately does NOT nest Horizon's `slideshow-component`: that engine collides with the
 * parent hero slideshow on three fronts — the `ref="slides[]"` collection, duplicate named CSS
 * view-timelines (`--slide-N`), and the parent slide's `on:click="/select/N"` (a tap would bubble
 * and navigate the hero). This component therefore:
 *   - crossfades stacked <img>s with pure opacity (no scroll-snap, no view-timeline, no scroller),
 *   - queries its own images directly (no `ref`) so it can't leak into the parent's slide refs,
 *   - has no interactive controls, so a tap just advances the parent hero (harmless).
 *
 * Autoplay coexistence with the parent hero ("independent + guidance" model): the gallery rotates
 * on its own short interval, but an IntersectionObserver pauses it whenever its slide is scrolled
 * out of view — i.e. it only animates while it's the active hero slide. It also pauses on tab-hide,
 * hover and focus, and honors prefers-reduced-motion. It rotates on mobile too, paired with the
 * always-available pause control (WCAG 2.2.2).
 *
 * @extends {Component}
 */
export class HeroSlideGallery extends Component {
  #current = 0;
  /** @type {number|undefined} */
  #interval = undefined;
  /** @type {IntersectionObserver|undefined} */
  #observer = undefined;
  #inView = false;
  /** True once the user has explicitly paused via the control (overrides incidental resume). */
  #userPaused = false;
  /** @type {HTMLButtonElement|null} */
  #toggleBtn = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    // Pause while a keyboard user is focused inside (WCAG 2.2.2 pause mechanism).
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // User pause/play control (WCAG 2.2.2). Optional — only rendered when autoplay is on.
    this.#toggleBtn = this.querySelector('[data-gallery-toggle]');
    this.#toggleBtn?.addEventListener('click', this.#handleToggle);

    // Establish initial visibility (matches the CSS :first-child fallback before hydration).
    this.current = 0;

    // Only rotate while the slide is actually on screen (the parent hero scrolls inactive
    // slides out of view, so this doubles as "pause when not the active slide").
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.#inView = entry.isIntersecting;
            this.#inView ? this.resume() : this.suspend();
          }
        },
        { threshold: 0.5 }
      );
      this.#observer.observe(this);
    } else {
      this.#inView = true;
      this.play();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    this.#observer?.disconnect();
    this.#toggleBtn?.removeEventListener('click', this.#handleToggle);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  get images() {
    return Array.from(this.querySelectorAll('.hero-slide__gallery-img'));
  }

  next() {
    const images = this.images;
    if (images.length === 0) return;
    const target = images[(this.#current + 1) % images.length];
    if (target.classList.contains('hero-slide__gallery-img--deferred') || !target.complete) {
      this.#revealNext();
      return;
    }
    this.current += 1;
    this.#revealNext();
  }

  #revealNext = () => {
    this.#reveal(this.#current + 1);
  };

  /** @param {number} index */
  #reveal(index) {
    const images = this.images;
    if (images.length === 0) return;
    let i = index % images.length;
    if (i < 0) i += images.length;
    images[i].classList.remove('hero-slide__gallery-img--deferred');
  }

  /**
   * Start automatic rotation. No-op when autoplay is off, reduced-motion is preferred,
   * or while the gallery isn't on screen. Rotates on mobile too (with the visible pause control),
   * so phone visitors actually see every image rather than only the first.
   */
  play() {
    if (this.#userPaused) return;
    if (!this.autoplay || this.prefersReducedMotion || !this.#inView) return;
    if (this.images.length < 2) return;

    this.suspend();
    this.#revealNext();
    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, this.intervalMs);
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    this.play();
  }

  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get autoplay() {
    return this.hasAttribute('autoplay') && !Number.isNaN(this.intervalMs);
  }

  get intervalMs() {
    const value = parseInt(`${this.getAttribute('autoplay')}`, 10);
    return Number.isNaN(value) ? 3000 : value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const images = this.images;
    if (images.length === 0) return;

    this.#current = current;

    let relativeIndex = current % images.length;
    if (relativeIndex < 0) relativeIndex += images.length;
    this.#reveal(relativeIndex);

    images.forEach((img, index) => {
      img.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #handleToggle = (event) => {
    // Keep the tap from bubbling to the parent hero's slide-select click handler.
    event.preventDefault();
    event.stopPropagation();

    this.#userPaused = !this.#userPaused;
    if (this.#userPaused) {
      this.suspend();
      this.setAttribute('data-paused', '');
      this.#toggleBtn?.setAttribute('aria-label', 'Play image gallery');
    } else {
      this.removeAttribute('data-paused');
      this.#toggleBtn?.setAttribute('aria-label', 'Pause image gallery');
      this.play();
    }
  };
}

if (!customElements.get('hero-slide-gallery')) {
  customElements.define('hero-slide-gallery', HeroSlideGallery);
}
